import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Upload, FileText, File, Trash2, Download, Edit2, 
  Search, Filter, FolderOpen, CheckCircle2, Clock, Eye, Send,
  AlertCircle, MoreVertical, Plus, X, Save, FileSignature
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import BlueprintIndicator from '@/components/Blueprint/BlueprintIndicator';
import HelpIcon from '@/components/HelpIcon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BrandLockup } from '@/components/brand/Brand';

const API = process.env.REACT_APP_BACKEND_URL;

// Category configuration with Keystone-themed colors
const CATEGORY_CONFIG = {
  onboarding: { name: 'Client Onboarding', icon: '👋', color: 'bg-[#E6DCD1] text-[#70170F]' },
  pre_design: { name: 'Pre-Design', icon: '📋', color: 'bg-[#70170F]/10 text-[#70170F]' },
  schematic: { name: 'Schematic Design', icon: '✏️', color: 'bg-[#BF7F3F]/10 text-[#BF7F3F]' },
  design_dev: { name: 'Design Development', icon: '📐', color: 'bg-[#70170F]/10 text-[#70170F]' },
  construction_docs: { name: 'Construction Documents', icon: '📄', color: 'bg-[#BF7F3F]/10 text-[#BF7F3F]' },
  contract: { name: 'Contract & Bidding', icon: '📝', color: 'bg-[#E6DCD1] text-[#70170F]' },
  construction_admin: { name: 'Construction Admin', icon: '🏗️', color: 'bg-[#BF7F3F]/10 text-[#BF7F3F]' },
  closeout: { name: 'Project Closeout', icon: '✅', color: 'bg-green-100 text-green-800' },
  iguide_floorplan: { name: 'iGuide Floor Plans', icon: '🏠', color: 'bg-purple-100 text-purple-800' },
  general: { name: 'General', icon: '📁', color: 'bg-gray-100 text-gray-800' },
};

export default function DocumentLibrary() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadRequiresSignature, setUploadRequiresSignature] = useState(false);
  
  // Edit dialog state
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Dark mode - synced with UserDashboard's keyMadeDarkMode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved === 'true';
  });

  useEffect(() => {
    const handleDarkModeChange = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved === 'true');
    };
    window.addEventListener('storage', handleDarkModeChange);
    const interval = setInterval(handleDarkModeChange, 2000);
    return () => {
      window.removeEventListener('storage', handleDarkModeChange);
      clearInterval(interval);
    };
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (categoryFilter !== 'all') {
        params.category = categoryFilter;
      }
      const response = await axios.get(`${API}/api/document-templates`, { params });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      // Auto-fill name from filename
      if (!uploadName) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setUploadName(nameWithoutExt.replace(/[-_]/g, ' '));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName || !uploadCategory) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName);
      formData.append('description', uploadDescription);
      formData.append('category', uploadCategory);
      formData.append('requires_signature', uploadRequiresSignature);

      await axios.post(`${API}/api/document-templates`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Template uploaded successfully');
      setShowUpload(false);
      resetUploadForm();
      fetchTemplates();
    } catch (error) {
      console.error('Error uploading template:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload template');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadName('');
    setUploadDescription('');
    setUploadCategory('general');
    setUploadRequiresSignature(false);
  };

  const handleDownload = async (template) => {
    try {
      const response = await axios.get(`${API}/api/document-templates/${template.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', template.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download template');
    }
  };

  const handleDelete = async (template) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/api/document-templates/${template.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await axios.put(`${API}/api/document-templates/${editingTemplate.id}`, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        category: editingTemplate.category,
        requires_signature: editingTemplate.requires_signature,
      });
      
      toast.success('Template updated');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Failed to update template');
    }
  };

  // Filter templates by search
  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group templates by category
  const templatesByCategory = filteredTemplates.reduce((acc, t) => {
    const cat = t.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Professional black/grey dark mode with Keystone accents
  const bgClass = darkMode ? 'bg-black text-[#E5E2DD]' : 'bg-[#FDFCFB]';
  const cardClass = darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A]' : 'bg-white border-[#E5E2DD]';
  const inputClass = darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] text-[#E5E2DD]' : '';

  return (
    <div className={`min-h-screen ${bgClass}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'} px-6 py-4`}>
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className={`flex items-center gap-2 text-sm mb-3 ${darkMode ? 'text-[#6B6B6B]' : 'text-muted-foreground'}`}>
            <button 
              onClick={() => navigate('/dashboard')} 
              className={`hover:underline ${darkMode ? 'hover:text-[#E5E2DD]' : 'hover:text-foreground'}`}
            >
              My Desk
            </button>
            <span>/</span>
            <span className={darkMode ? 'text-[#E5E2DD]' : 'text-foreground'}>Document Library</span>
          </nav>
          
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className={darkMode ? 'hover:bg-[#1A1A1A] text-[#8A8A8A]' : ''}>
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                My Desk
              </Button>
              <div className={`border-l pl-4 ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
                <div className="flex items-center gap-3">
                  <HelpIcon elementId="document-library" position="inline" />
                  <h1 className={`text-2xl font-heading ${darkMode ? 'text-[#E5E2DD]' : 'text-[#70170F]'}`}>Document Library</h1>
                  <BlueprintIndicator sectionId="documents-overview" tooltip="Learn about Documents" />
                  <span className="flex flex-col leading-tight">
                    <BrandLockup onDark={darkMode} testId="doclib-brand" />
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Manage document templates for your projects
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => navigate('/documents/contractor-agreement/new')} className={`whitespace-nowrap ${darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}`} data-testid="contractor-agreement-btn">
                <FileSignature className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Contractor Agreement</span>
                <span className="sm:hidden">Agreement</span>
              </Button>
              <Button variant="outline" onClick={() => navigate('/documents/editor/new')} className={`whitespace-nowrap ${darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}`}>
                <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Create from Scratch</span>
                <span className="sm:hidden">Create</span>
              </Button>
              <Button onClick={() => setShowUpload(true)} data-testid="upload-template-btn" className="bg-[#70170F] hover:bg-[#70170F]/90 text-white whitespace-nowrap">
                <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="hidden sm:inline">Upload Template</span>
                <span className="sm:hidden">Upload</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 ${inputClass}`}
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className={`w-[200px] ${inputClass}`}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  {config.icon} {config.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className={`text-center py-12 rounded-lg border ${cardClass}`}>
            <FolderOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No templates yet</h3>
            <p className="text-muted-foreground mb-4">
              Upload your first document template to get started
            </p>
            <Button onClick={() => setShowUpload(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Template
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(CATEGORY_CONFIG).map(([catKey, catConfig]) => {
              const catTemplates = templatesByCategory[catKey] || [];
              if (catTemplates.length === 0 && categoryFilter !== 'all') return null;
              if (catTemplates.length === 0) return null;
              
              return (
                <div key={catKey}>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xl">{catConfig.icon}</span>
                    <h2 className={`text-lg font-heading ${darkMode ? 'text-white' : ''}`}>
                      {catConfig.name}
                    </h2>
                    <Badge variant="secondary" className="ml-2">
                      {catTemplates.length}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {catTemplates.map(template => (
                      <Card 
                        key={template.id} 
                        className={`${cardClass} cursor-pointer transition-all hover:shadow-md ${darkMode ? 'hover:border-[#3A3A3A]' : 'hover:border-[#BC5D41]/30'}`} 
                        data-testid={`template-card-${template.id}`}
                        onClick={() => navigate(`/documents/editor/${template.id}`)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              {template.file_type === 'pdf' ? (
                                <div className="w-10 h-10 rounded bg-red-100 dark:bg-red-900 flex items-center justify-center">
                                  <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
                                </div>
                              ) : template.file_type === 'docx' ? (
                                <div className="w-10 h-10 rounded bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                  <File className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                              ) : (
                                <div className={`w-10 h-10 rounded flex items-center justify-center ${darkMode ? 'bg-[#70170F]/20' : 'bg-[#BC5D41]/10'}`}>
                                  <FileText className="w-5 h-5 text-[#BC5D41]" />
                                </div>
                              )}
                              <div>
                                <CardTitle className="text-base">{template.name}</CardTitle>
                                <p className="text-xs text-muted-foreground">
                                  {template.file_type ? `${template.file_type.toUpperCase()} • ${formatFileSize(template.file_size)}` : 'Template'}
                                </p>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/documents/editor/${template.id}`); }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Open in Editor
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownload(template); }}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Download
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingTemplate(template); }}>
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit Details
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => { e.stopPropagation(); handleDelete(template); }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {template.description && (
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {template.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            {template.requires_signature && (
                              <Badge variant="outline" className="text-xs">
                                <FileSignature className="w-3 h-3 mr-1" />
                                Requires Signature
                              </Badge>
                            )}
                            <Badge variant="secondary" className={`text-xs ${catConfig.color}`}>
                              {catConfig.name}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className={`max-w-md ${darkMode ? 'bg-gray-800 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>Upload Document Template</DialogTitle>
            <DialogDescription>
              Upload a PDF or Word document to use as a template
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Upload */}
            <div>
              <Label>Document File *</Label>
              <div className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center relative ${
                darkMode ? 'border-[#2A2A2A]' : 'border-gray-300'
              } ${uploadFile ? 'border-green-500 bg-green-50 dark:bg-green-900/20' : ''}`}>
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="text-sm">{uploadFile.name}</span>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => setUploadFile(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-2">
                      Drag & drop or click to upload
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF or Word documents only
                    </p>
                  </>
                )}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ position: 'absolute', top: 0, left: 0 }}
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="e.g., Design Agreement"
                className={`mt-1 ${inputClass}`}
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="template-desc">Description</Label>
              <Input
                id="template-desc"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Brief description of this document"
                className={`mt-1 ${inputClass}`}
              />
            </div>

            {/* Category */}
            <div>
              <Label>Category *</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className={`mt-1 ${inputClass}`}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.icon} {config.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Requires Signature */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="requires-sig"
                checked={uploadRequiresSignature}
                onCheckedChange={setUploadRequiresSignature}
              />
              <Label htmlFor="requires-sig" className="text-sm cursor-pointer">
                This document requires client signature
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadName}>
              {uploading ? 'Uploading...' : 'Upload Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className={`max-w-md ${darkMode ? 'bg-gray-800 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          
          {editingTemplate && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  className={`mt-1 ${inputClass}`}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={editingTemplate.description || ''}
                  onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                  className={`mt-1 ${inputClass}`}
                />
              </div>

              <div>
                <Label>Category</Label>
                <Select 
                  value={editingTemplate.category} 
                  onValueChange={(v) => setEditingTemplate({...editingTemplate, category: v})}
                >
                  <SelectTrigger className={`mt-1 ${inputClass}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        {config.icon} {config.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={editingTemplate.requires_signature}
                  onCheckedChange={(checked) => setEditingTemplate({...editingTemplate, requires_signature: checked})}
                />
                <Label className="text-sm cursor-pointer">
                  Requires client signature
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTemplate}>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
